import { EmploymentSupport } from './types';

export function hasEmploymentSupportData(data?: EmploymentSupport | null): data is EmploymentSupport {
  return Boolean(data?.coverage.length && data.monthlyPayments.some(payment => payment.amountSek !== 0));
}

export function getEmploymentSupportTypes(data: EmploymentSupport) {
  // Coverage describes the publication, while payments belong to this company.
  // Keep negative adjustments and types whose payments cancel out over time.
  return Array.from(new Set(data.monthlyPayments
    .filter(payment => payment.amountSek !== 0)
    .map(payment => payment.supportType))).sort();
}

export function getEmploymentSupportSourceInfo(data: EmploymentSupport) {
  const months = data.coverage.map(month => month.month.slice(0, 7)).sort();
  const firstMonth = months[0];
  const lastMonth = months[months.length - 1];
  const throughDate = lastMonth
    ? new Date(Date.UTC(Number(lastMonth.slice(0, 4)), Number(lastMonth.slice(5, 7)), 0)).toISOString().slice(0, 10)
    : '';
  const refreshedOn = data.coverage.map(month => month.importedAt.slice(0, 10)).sort().slice(-1)[0] ?? '';
  return { firstMonth, lastMonth, throughDate, refreshedOn };
}

export function buildEmploymentSupportTrend(data: EmploymentSupport, allHistory: boolean, selectedType = '') {
  const types = getEmploymentSupportTypes(data);
  const displayedTypes = selectedType ? types.filter(type => type === selectedType) : types;
  const payments = new Map(data.monthlyPayments.map(p => [`${p.month.slice(0, 7)}|${p.supportType}`, p.amountSek]));
  const source = getEmploymentSupportSourceInfo(data);
  const first = allHistory ? source.firstMonth : [source.firstMonth, data.windowStartMonth.slice(0, 7)].sort().slice(-1)[0];
  const last = allHistory ? source.lastMonth : [source.lastMonth, data.windowEndMonth.slice(0, 7)].sort()[0];
  const rows: { month: string; [key: string]: string | number }[] = [];
  let netAmountCents = 0;
  if (!source.firstMonth || !source.lastMonth) return { types: displayedTypes, rows, netAmount: 0 };
  if (!first || !last) return { types: displayedTypes, rows, netAmount: 0 };
  const start = Number(first.slice(0, 4)) * 12 + Number(first.slice(5, 7)) - 1;
  const end = Number(last.slice(0, 4)) * 12 + Number(last.slice(5, 7)) - 1;
  for (let index = start; index <= end; index++) {
    const month = `${Math.floor(index / 12)}-${String(index % 12 + 1).padStart(2, '0')}`;
    const row: typeof rows[number] = { month };
    for (let i = 0; i < displayedTypes.length; i++) {
      const type = displayedTypes[i];
      const amount = payments.get(`${month}|${type}`) ?? 0;
      row[`type${i}`] = amount;
      netAmountCents += Math.round(amount * 100);
    }
    rows.push(row);
  }
  return { types: displayedTypes, rows, netAmount: netAmountCents / 100 };
}
